import { Button, Col, Input, Row, Select, Space, Tag } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useState } from 'react';
import { useAuthedMutation } from 'utils/qlAuth';
import gql from 'graphql-tag';
import { StoryFramesModel } from 'interface/models';
import { CameraOutlined, ExperimentOutlined, LoadingOutlined } from '@ant-design/icons';
import { comparePrompts } from 'queries/ai-threshold';

interface StoryFrameProps {
  frame: StoryFramesModel;
  storyId: number;
  displayType?: 'text' | 'image' | string;

  onChange: (frame: StoryFramesModel) => void;
  onReload: () => void;
}

const presetStyles = [
  { value: 'DYNAMIC', label: 'Dynamic', description: 'Динамичный' },
  { value: 'CINEMATIC', label: 'Cinematic', description: 'Кинематографичный' },
  { value: 'CINEMATIC_CLOSEUP', label: 'Cinematic Close-Up', description: 'Крупный план' },
  { value: 'MOODY', label: 'Moody', description: 'Мрачный' },
  { value: 'HDR', label: 'HDR', description: 'Высокий динамический диапазон' },
  { value: 'LONG_EXPOSURE', label: 'Long Exposure', description: 'Длинная выдержка' },
  { value: 'VIBRANT', label: 'Vibrant', description: 'Яркий' },
  { value: 'FILM', label: 'Film', description: 'Пленочный' },
  { value: 'MONOCHROME', label: 'Monochrome', description: 'Монохромный' },
  { value: 'CREATIVE', label: 'Creative', description: 'Креативный' },
  { value: 'MACRO', label: 'Macro', description: 'Макро' },
  { value: 'BOKEH', label: 'Bokeh', description: 'Размытый задний план с мягкими световыми пятнами. Отлично подчёркивает главный объект.' },
  { value: 'PORTRAIT', label: 'Portrait', description: 'Портретный' },
  { value: 'RETRO', label: 'Retro', description: 'Ретро' },
  { value: 'NEUTRAL', label: 'Neutral', description: 'Нейтральный' },
  { value: 'FOOD', label: 'Food', description: 'Еда' },
  { value: 'FASHION', label: 'Fashion', description: 'Мода' },
  { value: 'STOCK_PHOTO', label: 'Stock Photo', description: 'Стоковая фотография' },
  { value: 'UNPROCESSED', label: 'Unprocessed', description: 'Необработанный' },
];

export const StoryFrame = (props: StoryFrameProps) => {
  const frame = props.frame;

  let style_50vw = 'calc(50vw - 117px)';
  let style_imgSize = '150px';
  let hideForImage_displayType = {};
  let image_MaxWidth = '100%';
  if (props.displayType === 'image-big') {
    style_50vw = 'calc(50vw - 60px)';
    style_imgSize = 'calc(50vw - 60px)';
    hideForImage_displayType = { display: 'none' };
  }
  if (props.displayType === 'image-medium') {
    style_50vw = 'calc(70vw - 60px)';
    style_imgSize = 'calc(30vw - 60px)';
    hideForImage_displayType = { display: 'none' };
  }
  if (props.displayType === 'text-drawer') {
    style_50vw = 'calc(25vw - 36px)';
    style_imgSize = 'calc(25vw - 60px)';
    hideForImage_displayType = {};
    image_MaxWidth = '40%';
  }

  const [updateFrame] = useAuthedMutation(
    gql`
      mutation StoryController_updateFrame($frameId: Int!, $input: JSON!) {
        StoryController_updateFrame(frameId: $frameId, config: $input) {
          id
        }
      }
    `,
  );
  const [genPromt] = useAuthedMutation(
    gql`
      mutation StoryboardController_genPromt($frameId: Int!, $promt: String) {
        StoryboardController_genPromt(frameId: $frameId, promt: $promt) {
          promt
        }
      }
    `,
  );
  const [genMotionPromt] = useAuthedMutation(
    gql`
      mutation StoryboardController_genMotionPromt($frameId: Int!, $promt: String) {
        StoryboardController_genMotionPromt(frameId: $frameId, promt: $promt) {
          motionPromt
        }
      }
    `,
  );
  const [genImage] = useAuthedMutation(
    gql`
      mutation StoryboardController_genImage($frameId: Int!, $promt: String, $config: JSON!) {
        StoryboardController_genImage(frameId: $frameId, promt: $promt, config: $config) {
          promt
        }
      }
    `,
  );
  const [status, setStatus] = useState(null);
  const [tts, setTts] = useState(frame.tts);
  const [promt, setPromt] = useState(frame.promt + ` ..${frame.id}..`);
  const [initText, setInitText] = useState(frame.initText);
  const [motion, setMotion] = useState(frame.motion);
  const [motionPromt, setMotionPromt] = useState(frame.motionPromt);
  const [presetStyle, setPresetStyle] = useState(frame.presetStyle || 'CINEMATIC');

  const handleSave = async (field: 'tts' | 'promt' | 'initText' | 'presetStyle' | 'motion' | 'motionPromt', value: string) => {
    if (comparePrompts(frame[field], value)) {
      return;
    }
    setStatus('load');
    await updateFrame({
      variables: {
        frameId: frame.id,
        input: { [field]: value },
      },
    });
    props.onChange({ ...frame, [field]: value });
    setStatus(null);
  };

  if (frame.status === 'hidden') {
    return (
      <>
        {' '}
        {frame.initText}
        <br />
        <br />
      </>
    );
  }

  return (
    <>
      <br />
      <br />
      <Row gutter={[16, 16]}>
        <Col flex="auto">
          <Space direction="vertical">
            <Space direction="horizontal" style={hideForImage_displayType}>
              <Select
                onChange={value => {
                  setPresetStyle(value);
                  handleSave('presetStyle', value);
                }}
                defaultValue={presetStyle}
                style={{ width: style_50vw }}
              >
                {presetStyles.map(style => (
                  <Select.Option key={style.value} value={style.value}>
                    {style.label} <i>{style.description}</i>
                  </Select.Option>
                ))}
              </Select>
              <Input
                style={{ width: style_50vw }}
                type="text"
                value={tts}
                onChange={e => {
                  const value = e.target.value;
                  setTts(value);
                }}
                onBlur={e => {
                  const value = e.target.value;
                  setTts(value);
                  handleSave('tts', value);
                }}
                placeholder="TTS"
              />
            </Space>

            <Space direction="horizontal">
              <TextArea
                style={{ width: style_50vw, ...hideForImage_displayType }}
                value={initText}
                onChange={e => {
                  const value = e.target.value;
                  setInitText(value);
                }}
                onBlur={async e => {
                  const value = e.target.value;
                  setInitText(value);
                  await handleSave('initText', value);
                  if (!comparePrompts(frame.initText, value)) {
                    setStatus('load');
                    const res = await genPromt({
                      variables: {
                        frameId: frame.id,
                        promt: value,
                      },
                    });
                    if (res.data?.StoryboardController_genPromt) {
                      setPromt(res.data?.StoryboardController_genPromt.promt);
                    }
                    setStatus(null);
                  }
                }}
                placeholder="Initial Text (initText)"
              />
              <TextArea
                style={{ width: style_50vw }}
                value={motion}
                onChange={e => {
                  const value = e.target.value;
                  setMotion(value);
                }}
                onBlur={async e => {
                  const value = e.target.value;
                  setMotion(value);
                  await handleSave('motion', value);
                  if (!comparePrompts(frame.motion, value)) {
                    setStatus('load');
                    const res = await genMotionPromt({
                      variables: {
                        frameId: frame.id,
                        promt: value,
                      },
                    });
                    if (res.data?.StoryboardController_genMotionPromt) {
                      setMotionPromt(res.data?.StoryboardController_genMotionPromt.motionPromt);
                    }
                    setStatus(null);
                  }
                }}
                placeholder="Движение (motion)"
              />
            </Space>

            <Space direction="horizontal">
              <TextArea
                style={{ width: style_50vw, ...hideForImage_displayType }}
                value={promt}
                onChange={e => {
                  const value = e.target.value;
                  setPromt(value);
                }}
                onBlur={e => {
                  const value = e.target.value;
                  setPromt(value);
                  handleSave('promt', value);
                }}
                placeholder="Prompt"
              />
              <TextArea
                style={{ width: style_50vw }}
                value={motionPromt}
                onChange={e => {
                  const value = e.target.value;
                  setMotionPromt(value);
                }}
                onBlur={e => {
                  const value = e.target.value;
                  setMotionPromt(value);
                  handleSave('motionPromt', value);
                }}
                placeholder="Motion promt"
              />
            </Space>
            <br />
          </Space>
        </Col>
        <Col flex={style_imgSize} style={{ textAlign: 'center' }}>
          <Space direction="vertical">
            <Space direction="horizontal">
              <Button
                title="Big image"
                onClick={async e => {
                  setStatus('load');
                  await genImage({
                    variables: {
                      frameId: frame.id,
                      promt: promt,
                      config: {
                        presetStyle: presetStyle,
                        // Add your config here
                      },
                    },
                  });
                  props.onReload();
                  setStatus(null);
                }}
                style={{ color: 'green' }}
              >
                <CameraOutlined />
              </Button>
              <Button
                title="Small preview"
                onClick={async e => {
                  setStatus('load');
                  await genImage({
                    variables: {
                      frameId: frame.id,
                      promt: promt,
                      config: {
                        presetStyle: presetStyle,
                        // Add your config here
                        height: 512,
                        width: 912,
                        alchemy: false,
                        photoReal: false,
                      },
                    },
                  });
                  props.onReload();
                  setStatus(null);
                }}
              >
                <ExperimentOutlined />
              </Button>
              {status === 'load' && <Tag color="red">{<LoadingOutlined />}</Tag>}
            </Space>
            {frame.images.map((img, index) => {
              return (
                <>
                  <Button
                    key={index}
                    style={{
                      all: 'unset',
                      display: 'inline-block',
                      width: '100%',
                      maxWidth: image_MaxWidth,
                      margin: '0px',
                      borderRadius: '5px',
                      border: '1px solid #ccc',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      cursor: 'pointer',
                      transition: 'transform 0.2s',
                      padding: '0px',
                    }}
                    onClick={() => {
                      window.open(img, '_blank');
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        window.open(img, '_blank');
                      }
                    }}
                    onMouseOver={e => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onFocus={e => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    onBlur={e => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    onDragStart={e => {
                      e.preventDefault();
                      return false;
                    }}
                  >
                    <img
                      src={img}
                      alt="preview"
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '5px',
                      }}
                    />
                  </Button>
                </>
              );
            })}
          </Space>
        </Col>
      </Row>
    </>
  );
};
